import React, { useState } from 'react';
import {
  Button,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  Divider,
  Modal,
  Row,
  Col,
  InputNumber,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DragOutlined,
} from '@ant-design/icons';
import type { CustomFieldConfig, CustomFieldOption } from '../services/missionTypeService';

const { Text } = Typography;

interface CustomFieldBuilderProps {
  value?: CustomFieldConfig[];
  onChange?: (fields: CustomFieldConfig[]) => void;
  disabled?: boolean;
}

const FIELD_TYPE_LABELS: Record<CustomFieldConfig['type'], string> = {
  text: 'Texte',
  number: 'Nombre',
  select: 'Liste déroulante',
  checkbox: 'Case à cocher',
  textarea: 'Zone de texte',
  date: 'Date',
};

const FIELD_TYPE_COLORS: Record<CustomFieldConfig['type'], string> = {
  text: 'blue',
  number: 'green',
  select: 'purple',
  checkbox: 'orange',
  textarea: 'cyan',
  date: 'geekblue',
};

/**
 * Converts a label string to a snake_case slug.
 */
function toSlug(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);
}

const CustomFieldBuilder: React.FC<CustomFieldBuilderProps> = ({
  value = [],
  onChange,
  disabled = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [optionsList, setOptionsList] = useState<CustomFieldOption[]>([]);
  const [form] = Form.useForm();

  const fields = value;

  const triggerChange = (newFields: CustomFieldConfig[]) => {
    onChange?.(newFields);
  };

  const handleAddClick = () => {
    setEditingIndex(null);
    setOptionsList([]);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEditClick = (index: number) => {
    const field = fields[index];
    setEditingIndex(index);
    setOptionsList(field.options || []);
    form.setFieldsValue({
      ...field,
    });
    setModalOpen(true);
  };

  const handleDeleteClick = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    triggerChange(newFields);
  };

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentName = form.getFieldValue('name');
    // Only auto-derive name if user hasn't manually edited it or it's empty
    if (!currentName || currentName === toSlug(form.getFieldValue('label') || '')) {
      form.setFieldValue('name', toSlug(e.target.value));
    }
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const fieldType = values.type as CustomFieldConfig['type'];

      const newField: CustomFieldConfig = {
        name: values.name,
        label: values.label,
        type: fieldType,
        required: values.required ?? false,
        placeholder: values.placeholder || undefined,
      };

      if (fieldType === 'select') {
        newField.options = optionsList;
      }

      if (fieldType === 'number' && (values.min !== undefined || values.max !== undefined)) {
        newField.validation = {};
        if (values.min !== undefined && values.min !== null) newField.validation.min = values.min;
        if (values.max !== undefined && values.max !== null) newField.validation.max = values.max;
      }

      let newFields: CustomFieldConfig[];
      if (editingIndex !== null) {
        newFields = fields.map((f, i) => (i === editingIndex ? newField : f));
      } else {
        newFields = [...fields, newField];
      }

      triggerChange(newFields);
      setModalOpen(false);
      form.resetFields();
      setOptionsList([]);
      setEditingIndex(null);
    });
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    form.resetFields();
    setOptionsList([]);
    setEditingIndex(null);
  };

  const addOption = () => {
    setOptionsList([...optionsList, { label: '', value: '' }]);
  };

  const updateOption = (index: number, key: 'label' | 'value', val: string) => {
    const updated = optionsList.map((opt, i) =>
      i === index ? { ...opt, [key]: val } : opt
    );
    setOptionsList(updated);
  };

  const removeOption = (index: number) => {
    setOptionsList(optionsList.filter((_, i) => i !== index));
  };

  return (
    <div>
      {fields.length === 0 && (
        <div
          style={{
            padding: '16px',
            textAlign: 'center',
            backgroundColor: '#fafafa',
            border: '1px dashed #d9d9d9',
            borderRadius: '6px',
            marginBottom: '12px',
          }}
        >
          <Text type="secondary">Aucun champ personnalisé défini.</Text>
        </div>
      )}

      {fields.map((field, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            marginBottom: '8px',
            border: '1px solid #f0f0f0',
            borderRadius: '6px',
            backgroundColor: '#fff',
          }}
        >
          <Space>
            <DragOutlined style={{ color: '#bbb', cursor: disabled ? 'default' : 'grab' }} />
            <div>
              <Space size="small">
                <Text strong>{field.label}</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  ({field.name})
                </Text>
              </Space>
              <div>
                <Tag color={FIELD_TYPE_COLORS[field.type]} style={{ marginRight: '4px' }}>
                  {FIELD_TYPE_LABELS[field.type]}
                </Tag>
                {field.required && <Tag color="red">Requis</Tag>}
              </div>
            </div>
          </Space>

          {!disabled && (
            <Space>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditClick(index)}
              />
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteClick(index)}
              />
            </Space>
          )}
        </div>
      ))}

      {!disabled && (
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          onClick={handleAddClick}
          style={{ marginTop: '8px' }}
        >
          Ajouter un champ
        </Button>
      )}

      <Modal
        title={editingIndex !== null ? 'Modifier le champ' : 'Ajouter un champ personnalisé'}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={editingIndex !== null ? 'Modifier' : 'Ajouter'}
        cancelText="Annuler"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="label"
                label="Libellé"
                rules={[{ required: true, message: 'Le libellé est requis' }]}
              >
                <Input placeholder="Ex: Nom du destinataire" onChange={handleLabelChange} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Clé (slug)"
                rules={[
                  { required: true, message: 'La clé est requise' },
                  { pattern: /^[a-z0-9_]+$/, message: 'Lettres minuscules, chiffres et _ uniquement' },
                ]}
              >
                <Input placeholder="Ex: recipient_name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true, message: 'Le type est requis' }]}
              >
                <Select placeholder="Sélectionner un type">
                  {(Object.keys(FIELD_TYPE_LABELS) as CustomFieldConfig['type'][]).map((t) => (
                    <Select.Option key={t} value={t}>
                      {FIELD_TYPE_LABELS[t]}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="required" label="Requis" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="placeholder" label="Placeholder (optionnel)">
            <Input placeholder="Ex: Entrez le nom..." />
          </Form.Item>

          {/* Options section for select type */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
            {({ getFieldValue }) =>
              getFieldValue('type') === 'select' ? (
                <div>
                  <Divider orientation="left" style={{ fontSize: '13px' }}>
                    Options de la liste
                  </Divider>
                  {optionsList.map((opt, i) => (
                    <Row key={i} gutter={8} style={{ marginBottom: '8px' }}>
                      <Col span={10}>
                        <Input
                          placeholder="Libellé"
                          value={opt.label}
                          onChange={(e) => updateOption(i, 'label', e.target.value)}
                        />
                      </Col>
                      <Col span={10}>
                        <Input
                          placeholder="Valeur"
                          value={String(opt.value)}
                          onChange={(e) => updateOption(i, 'value', e.target.value)}
                        />
                      </Col>
                      <Col span={4}>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeOption(i)}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={addOption}
                    style={{ width: '100%' }}
                  >
                    Ajouter une option
                  </Button>
                </div>
              ) : null
            }
          </Form.Item>

          {/* Min/Max section for number type */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
            {({ getFieldValue }) =>
              getFieldValue('type') === 'number' ? (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="min" label="Valeur minimale">
                      <InputNumber style={{ width: '100%' }} placeholder="0" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="max" label="Valeur maximale">
                      <InputNumber style={{ width: '100%' }} placeholder="100" />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomFieldBuilder;
